import { computed, Injectable } from '@angular/core';
import {
  ActiveLookup,
  Association,
  AssociationKeys,
  ChangeSourceType,
  DietaryConsideration,
  GuestData,
  GuestEvents,
  GuestKeys,
  GuestStatus,
  LocalDateTimeString,
  TShirtSizes,
} from 'ag-common-lib/public-api';
import { BaseFormService, ConferenceGuestsService, LookupsService } from 'ag-common-svc/public-api';
import { confirm } from 'devextreme/ui/dialog';
import { AttendeeGuestsService } from '../attendee-guests.service';
import { ValidationCallbackData } from 'devextreme/common';
import { differenceInYears, isValid, toDate } from 'date-fns';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { AttendeeDetailsModalService } from '../../attendee-details-modal.service';
import { cloneDeep, merge } from 'lodash';

@Injectable()
export class AttendeeGuestModalService extends BaseFormService<GuestData> {
  conferenceGuestExcludedSections$ = this.attendeeGuestsService.conferenceGuestExcludedSections$;

  private _suffixesLookup: ActiveLookup[];
  private _sourceType = ChangeSourceType.regularArm;

  registrantGuestDbIdSig = computed(() => {
    const guest = this.formDataSig();
    const dbId = guest?.[BaseModelKeys.dbId];

    return dbId;
  });
  registrantAssociationsSig = computed<Association[]>(() => {
    const agentAssociations = this.attendeeDetailsModalService.agentAssociationsSig();
    const registrantGuestDbId = this.registrantGuestDbIdSig();
    const registrantGuests = this.attendeeGuestsService.registrantActiveGuestsSig();
    const assignedAssociations = new Set();

    registrantGuests?.forEach(registrantGuest => {
      if (registrantGuest?.[BaseModelKeys.dbId] === registrantGuestDbId) {
        return;
      }

      assignedAssociations.add(this.getAssociationKey(registrantGuest));
    });
    const items = [];

    agentAssociations?.forEach(association => {
      const associationKey = this.getAssociationKey(association);

      if (assignedAssociations.has(associationKey)) {
        return;
      }

      items.push(association);
    });

    return items;
  });

  constructor(
    private conferenceGuestsService: ConferenceGuestsService,
    private attendeeGuestsService: AttendeeGuestsService,
    private lookupsService: LookupsService,
    private attendeeDetailsModalService: AttendeeDetailsModalService,
  ) {
    super();

    this.lookupsService.suffixesLookup$.subscribe(suffixes => (this._suffixesLookup = suffixes));
  }

  saveGuest = () => (this.formDataSig()[BaseModelKeys.dbId] ? this.updateGuest() : this.createGuest());

  cancelEdit = ({ event, component }): void => {
    if (!this.formChangesDetector?.hasChanges) {
      return;
    }

    event.cancel = true;

    const result = confirm('<i>Are you sure you want to Cancel without Saving?</i>', 'Confirm');
    result.then(dialogResult => {
      if (dialogResult) {
        this.formChangesDetector?.clear();
        component.instance.hide();
      }
    });
  };

  getFormData = (guest?: Partial<GuestData>): void => {
    const initialData = Object.assign(new GuestData(), guest);
    const tShirtsSizesProxy = this.prepareTShirtsSizesProxy(guest?.[AssociationKeys.tShirtSizes]);
    const dietaryConsiderationProxy = this.prepareDietaryConsiderationProxy(
      guest?.[AssociationKeys.dietaryConsideration],
    );
    const formData = new Proxy(
      Object.assign({}, initialData, {
        [AssociationKeys.tShirtSizes]: tShirtsSizesProxy,
        [AssociationKeys.dietaryConsideration]: dietaryConsiderationProxy,
      }),
      {
        set: (target, prop, value, receiver) => {
          const prevValue = target[prop];
          this.formChangesDetector.handleChange(prop, value, prevValue);
          Reflect.set(target, prop, value, receiver);

          this.formDataSig.update(data => data);

          return true;
        },
      },
    );

    this.formDataSig.set(formData);
  };

  onAssociationChanged = (association: Partial<Association>, associationDbId: string) => {
    const formData = this.formDataSig();

    if (associationDbId === this.formDataSig()?.[GuestKeys.associationDbId]) {
      return;
    }
    formData[GuestKeys.associationDbId] = associationDbId;

    const registrantGuests = this.attendeeGuestsService.registrantGuestsSig();
    const existedGuest = registrantGuests?.find(guest => guest?.[GuestKeys.associationDbId] === associationDbId);

    if (existedGuest) {
      const complimentaryConfiguration = formData?.[GuestKeys.complimentaryConfiguration];
      const updates = {
        [GuestKeys.complimentaryConfiguration]: complimentaryConfiguration,
        [GuestKeys.guestStatus]: GuestStatus.default,
        [GuestKeys.guestEvents]: [],
      };
      const payload = cloneDeep(Object.assign({}, existedGuest, updates));

      this.getFormData(payload);

      return;
    }

    if (formData?.[BaseModelKeys.dbId]) {
      const guest = Object.assign({}, new GuestData());

      this.getFormData(guest);
    }

    // TODO active guests updates
    const associationSuffixId = association?.[AssociationKeys.suffix];
    const associationFirstName = association?.[AssociationKeys.firstName];
    const associationLastName = association?.[AssociationKeys.lastName];
    const [suffix] =
      this._suffixesLookup.find(({ [BaseModelKeys.dbId]: dbId }) => dbId === associationSuffixId)?.description ?? '';
    const associationBadgeName = [associationFirstName, associationLastName, suffix].filter(Boolean).join(' ');

    Object.assign(this.formDataSig(), {
      [GuestKeys.associationDbId]: associationDbId,
      [GuestKeys.badgeName]: associationBadgeName,
    });
  };

  getAssociationKey = (association: Partial<GuestData>) => {
    const associationDbId = association?.[GuestKeys.associationDbId];

    if (associationDbId) {
      return associationDbId;
    }

    return association?.[BaseModelKeys.dbId] ?? null;
  };

  private updateGuest = () => {
    debugger;
    this.startProgress();
    const conferenceDbId = this.attendeeDetailsModalService.conferenceDbIdSig();
    const registrantDbId = this.attendeeDetailsModalService.registrantDbIdSig();
    // TODO active guests updates
    return this.conferenceGuestsService
      .update(
        conferenceDbId,
        registrantDbId,
        this.formDataSig()[BaseModelKeys.dbId],
        this.formDataSig(),
        false,
        this._sourceType,
      )
      .then(() => {
        const associationDbId = this.formDataSig()[GuestKeys.associationDbId];
        if (!!associationDbId) {
          // TODO active guests updates
          // this.agentAssociationsService.update(
          //   registrantDbId,
          //   associationDbId,
          //   pick(this.formDataSig(), [
          //     AssociationKeys.firstName,
          //     AssociationKeys.lastName,
          //     AssociationKeys.gender,
          //     AssociationKeys.dob,
          //     AssociationKeys.associationType,
          //     AssociationKeys.tShirtSizes,
          //     AssociationKeys.dietaryConsideration,
          //   ]),
          //   true,
          // );
        }
        this.formChangesDetector.clear();
      })
      .finally(() => {
        this.stopProgress();
      });
  };

  private createGuest = () => {
    debugger;
    this.startProgress();
    const conferenceDbId = this.attendeeDetailsModalService.conferenceDbIdSig();
    const registrantDbId = this.attendeeDetailsModalService.registrantDbIdSig();

    return this.conferenceGuestsService
      .create(conferenceDbId, registrantDbId, this.formDataSig(), false, this._sourceType)
      .then(() => {
        this.formChangesDetector.clear();
      })
      .finally(() => {
        this.stopProgress();
      });
  };

  hotelRulesValidationCallback = async (e: ValidationCallbackData) => {
    const currentGuestId = this.formDataSig()?.[BaseModelKeys.dbId];
    const guests = this.attendeeGuestsService.registrantActiveGuestsSig();

    if (guests?.length < 3) {
      return true;
    }

    const guestNumbers: {
      numberOfAdults: {
        [key in GuestEvents]?: number;
      };
      numberOfChildren: {
        [key in GuestEvents]?: number;
      };
    } = {
      numberOfAdults: {},
      numberOfChildren: {},
    };

    guests?.forEach(guest => {
      const guestEvents = guest?.[GuestKeys.guestEvents];
      const dob = guest?.[BaseModelKeys.dbId] === currentGuestId ? e.value : guest?.[AssociationKeys.dob];
      const age = this.calculateAge(dob);

      if (!age) {
        return;
      }

      if (age >= 13) {
        guestEvents?.forEach(event => {
          if (!guestNumbers?.numberOfAdults?.[event]) {
            guestNumbers.numberOfAdults[event] = 1; // Registrant is adult always
          }
          guestNumbers.numberOfAdults[event] += 1;
        });
        return;
      }

      if (age >= 2) {
        guestEvents?.forEach(event => {
          if (!guestNumbers?.numberOfChildren?.[event]) {
            guestNumbers.numberOfChildren[event] = 0;
          }
          guestNumbers.numberOfChildren[event] += 1;
        });
        return;
      }
    });

    const isEvery = Object.values(GuestEvents).every(guestEvent => {
      const numberOfAdults = guestNumbers?.numberOfAdults[guestEvent] ?? 0;
      const numberOfChildren = guestNumbers?.numberOfChildren[guestEvent] ?? 0;

      return (numberOfAdults <= 2 && numberOfChildren <= 2) || (numberOfAdults <= 3 && numberOfChildren <= 1);
    });

    return isEvery;
  };

  calculateAge = (birthDate: LocalDateTimeString): number => {
    if (!isValid(toDate(birthDate))) {
      return null;
    }

    return differenceInYears(new Date(), birthDate);
  };

  private prepareDietaryConsiderationProxy = (dietaryConsideration: Partial<DietaryConsideration>) => {
    return new Proxy(Object.assign({}, dietaryConsideration), {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange(
            [AssociationKeys.dietaryConsideration, prop].join('.'),
            value,
            prevValue,
          );
          Reflect.set(target, prop, value, receiver);
        }

        return true;
      },
    });
  };

  private prepareTShirtsSizesProxy = (tShirtsSizes: Partial<TShirtSizes>) => {
    return new Proxy(Object.assign({}, tShirtsSizes), {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange([AssociationKeys.tShirtSizes, prop].join('.'), value, prevValue);
          Reflect.set(target, prop, value, receiver);
        }

        return true;
      },
    });
  };
}
